import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, FolderKanban, Image as ImageIcon, BarChart3, LogOut, Inbox, HandCoins, Sparkles, Building2, ChevronDown, ChevronRight, TrendingUp, Network, Coins, FileEdit, Archive as ArchiveIcon, Moon, Users2, Scale, Radar, Wrench, Sliders, Zap, Sun, CalendarDays, Home as HomeIcon, BookOpen, Compass } from "lucide-react";

// Lean-Core primary navigation (2026-08-21).
// Mandate: reduce Founder-facing clutter to 7 primary slots + ADVANCED collapsible.
// Every hidden page remains routed and reachable via ADVANCED — nothing deleted.
const SECTIONS_BASE = [
  {
    label: "Primary",
    items: [
      { to: "/admin/dashboard",              label: "Today",             icon: Sun },
      { to: "/admin/living-calendar",        label: "Living Calendar",   icon: CalendarDays },
      { to: "/admin/discovery",              label: "Opportunities",     icon: Compass },
      { to: "/admin/sanctuary",              label: "Sanctuary",         icon: HomeIcon },
      { to: "/admin/people",                 label: "People",            icon: Users },
      { to: "/admin/story-media",            label: "Story / Media",     icon: ImageIcon },
      { to: "/admin/institutional-settings", label: "Admin",             icon: Sliders },
    ],
  },
  {
    label: "Advanced",
    items: [
      { to: "/admin/curriculum",             label: "Curriculum",             icon: Moon },
      { to: "/admin/populate",               label: "Institutional Stewardship", icon: TrendingUp },
      { to: "/admin/crm",                    label: "CRM (legacy)",           icon: Users },
      { to: "/admin/media",                  label: "Media Library",          icon: ImageIcon },
      { to: "/admin/inbox",                  label: "Applications",           icon: Inbox },
      { to: "/admin/partnerships",           label: "Partnerships",           icon: Building2 },
      { to: "/admin/projects",               label: "Projects",               icon: FolderKanban },
      { to: "/admin/programs",               label: "Programs",               icon: Network },
      { to: "/admin/grants",                 label: "Grant Center",           icon: HandCoins },
      { to: "/admin/campaigns",              label: "Campaigns",              icon: Sparkles },
      { to: "/admin/economic-commons",       label: "Economic Commons (Lab)", icon: Coins },
      { to: "/admin/content",                label: "Content Editor",         icon: FileEdit },
      { to: "/admin/archive",                label: "Archive",                icon: ArchiveIcon },
      { to: "/admin/staff",                  label: "AI Staff",               icon: Users2 },
      { to: "/admin/doctrine",               label: "Doctrine",               icon: Scale },
      { to: "/admin/institutional-profile",  label: "Institutional Profile",  icon: Building2 },
      { to: "/admin/analytics",              label: "Analytics",              icon: BarChart3 },
    ],
  },
];

// Founder-only section — appended when the current user is a founder.
const FOUNDER_ONLY = {
  label: "Founder",
  items: [
    { to: "/admin/execution", label: "Execution", icon: Zap },
    { to: "/admin/maintenance", label: "Maintenance", icon: Wrench },
  ],
};

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isFounder = (user?.role === "founder") || (user?.role === "admin");
  const SECTIONS = isFounder ? [...SECTIONS_BASE, FOUNDER_ONLY] : SECTIONS_BASE;
  // Primary + Founder groups default open; Advanced defaults collapsed so the
  // Founder's first-open experience is a lean 7-item surface.
  const [openSections, setOpenSections] = useState(() =>
    Object.fromEntries(SECTIONS.map(s => [s.label, s.label !== "Advanced"]))
  );
  const toggle = (label) => setOpenSections(prev => ({ ...prev, [label]: !prev[label] }));

  return (
    <div className="min-h-screen flex bg-admin-base">
      <aside className="w-64 shrink-0 bg-forest text-bone flex flex-col">
        <div className="p-6 border-b border-white/10">
          <Link to="/admin/dashboard" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-bone text-forest grid place-items-center font-serif text-lg">N</div>
            <div>
              <div className="font-serif text-lg">NEOS</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-bone/60">Admin Portal</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {SECTIONS.map((section) => (
            <div key={section.label} className="mb-3">
              <button
                onClick={() => toggle(section.label)}
                data-testid={`admin-section-${section.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="w-full flex items-center justify-between px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-bone/50 hover:text-bone/80 transition-colors"
              >
                <span>{section.label}</span>
                {openSections[section.label] ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              </button>
              {openSections[section.label] && (
                <div className="mt-1 space-y-0.5">
                  {section.items.map((it) => (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      data-testid={`admin-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-200 ${isActive ? "bg-bone/10 text-bone" : "text-bone/70 hover:bg-bone/5 hover:text-bone"}`}
                    >
                      <it.icon className="h-4 w-4" strokeWidth={1.5} />
                      {it.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-bone/60 mb-2 truncate">{user?.email}</div>
          <button data-testid="admin-logout-btn" onClick={async () => { await logout(); navigate("/admin/login"); }}
            className="w-full flex items-center gap-2 text-sm text-bone/80 hover:text-bone transition-colors">
            <LogOut className="h-4 w-4" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <div className="max-w-7xl mx-auto p-8">{children}</div>
      </div>
    </div>
  );
}
